/*
 * ttg-theme-adapter.js — Theme-Adapter (Dwell / Horizon)
 * ---------------------------------------------------------------------------
 * Der EINZIGE theme-spezifische Teil. Beim Theme-Wechsel wird nur diese Datei
 * neu geschrieben, nicht die Engine.
 *
 * Kapselt:
 *   - Cart-Handover B: bestehendes {% form 'product' %} füttern
 *       (hidden `id` setzen + `properties[...]` injizieren + form.requestSubmit)
 *   - Varianten-Sync (hidden id-Input des nativen <product-form-component>)
 *   - Cart-Event-Brücke (Theme `cart:update` → Engine `cartAdded`)
 *   - native PDP-Bedienelemente ausblenden (Body-Klasse `ttg-active`)
 *
 * Theme-Fakten (inspiziert):
 *   - Form: <product-form-component> umschließt {% form 'product' %} mit
 *     id "BuyButtons-ProductForm-<section.id>" und <input name="id" ref=variantId>
 *   - product-form.js submittet via fetch(Theme.routes.cart_add_url) mit
 *     new FormData(form) → alle properties[...] fahren automatisch mit
 *   - events.js: ThemeEvents.cartUpdate = 'cart:update'
 * ---------------------------------------------------------------------------
 */

// Hinweis: KEIN statischer `import` der Engine. Sie wird im connectedCallback
// dynamisch über die versionierte, server-gerenderte URL (data-engine-src)
// geladen — sonst liefert der Shopify-CDN nach Engine-Änderungen die alte,
// unversionierte Datei aus (Cache-Bug). Horizon hat bereits eine Importmap;
// wir legen bewusst keine zweite an.

const FORM_SELECTOR = 'product-form-component form[data-type="add-to-cart-form"]';

/** Notbremse: so lange warten wir höchstens auf einen hängenden Engine-Import,
 *  bevor der Konfigurator als gescheitert gilt. Ein hängender Import (blockiertes
 *  oder sehr langsames CDN) lehnt nie ab — ohne diese Frist bliebe das tote
 *  Skelett unbegrenzt stehen. */
const TTG_BOOT_TIMEOUT_MS = 8000;

class TTGCustomizerElement extends HTMLElement {
  async connectedCallback() {
    // Wiedereintritt (Theme-Editor rendert Sections neu, Element wird verschoben):
    // NICHT erneut mounten — das gäbe doppelte Bindungen und einen zweiten
    // Kaufknopf. Aber die Body-Klasse neu setzen, denn disconnectedCallback hat
    // sie entfernt; sonst stünden Konfigurator UND native Produktseite dauerhaft
    // nebeneinander.
    if (this._booted) {
      if (this.engine) document.body.classList.add('ttg-active');
      return;
    }
    this._booted = true;

    // Schema-Regel 4: Metafeld-Config mit unbekannter Version (oder unbrauchbaren
    // steps) → die Section fällt auf die Section-Settings zurück und markiert das
    // am Element. Hier nur sichtbar machen, damit der Fallback nicht still bleibt.
    const configFallback = this.dataset.ttgConfigFallback;
    if (configFallback) {
      console.warn('[ttg] Metafeld-Config übersprungen (' + configFallback + ') — Config kommt aus den Section-Settings (Fallback).');
    }

    // Notbremse scharf stellen, BEVOR irgendetwas Asynchrones passiert.
    this._bootTimer = setTimeout(() => this._fail('timeout'), TTG_BOOT_TIMEOUT_MS);

    const cfgEl = this.querySelector('script[type="application/json"][data-ttg-config]');
    const root = this.querySelector('[data-ttg-root]');
    if (!cfgEl || !root) {
      console.error('[ttg] Konfiguration oder Mount-Punkt fehlt');
      return this._fail('no-config');
    }

    let config;
    try {
      config = JSON.parse(cfgEl.textContent);
    } catch (e) {
      console.error('[ttg] Konfig-JSON ungültig', e);
      return this._fail('bad-config');
    }

    // Engine über die versionierte URL laden (Cache-Bust bei Engine-Änderungen).
    const engineSrc = this.dataset.engineSrc || new URL('./ttg-engine.js', import.meta.url).href;
    let TTGEngine;
    try {
      ({ TTGEngine } = await import(engineSrc));
    } catch (e) {
      console.error('[ttg] Engine konnte nicht geladen werden:', engineSrc, e);
      return this._fail('engine-load');
    }

    // Bauen und Mounten geschützt: eine Ausnahme hier hinterließ bisher das
    // SSR-Skelett (leere Schritte, Kaufbereich OHNE Warenkorb-Knopf) über der
    // Produktseite — sichtbar, aber nicht kaufbar.
    try {
      this.engine = new TTGEngine(config, { onSubmit: (payload) => this.addToCart(payload) });
      this.engine.mount(root);
    } catch (e) {
      console.error('[ttg] Konfigurator konnte nicht aufgebaut werden', e);
      return this._fail('mount');
    }
    // Erst wenn der Konfigurator nachweislich steht, die native Produktseite
    // ausblenden. Nachweis = ein aufgebauter Kaufknopf; ein leeres Gerüst zählt
    // ausdrücklich nicht. Bewusst über das GANZE Element geprüft (Kaufbereich
    // ODER Sticky-CTA) — das SSR-Skelett trägt selbst nirgends data-ttg="add",
    // der Knopf entsteht ausschließlich beim Engine-Mount.
    if (!this.querySelector('[data-ttg="add"]')) {
      console.error('[ttg] Konfigurator ohne Kaufknopf aufgebaut — native Produktseite bleibt stehen');
      return this._fail('incomplete');
    }

    this._bootDone();
    this.classList.add('ttg-ready');
    document.body.classList.add('ttg-active');

    this.bindCartEvents();
    this.setupGallery();
    this.setMiniInsetTop();
    this.setupNoBrainer();
    this.setupMetrics();
  }

  /**
   * No-Brainer-Blöcke der Mantelseite (Briefing Conversion-Lecks Teil 4, 21.09.2026). Die Section rendert
   * die Blöcke (nur Mantel-Templates); hier steht der Teil, den Liquid nicht wissen kann:
   *   (a) Dreier-Streifen: Schritt 1 „Motiv wählen" gilt als erledigt, wenn das Motiv aus einem Deep-Link
   *       (?ttg-motif) oder der UTM-Zuordnung (config.utmMap) vorbelegt ist — Engine-Flag _motifFromUrl.
   *       Der Hinweis „Dein Motiv ist schon drauf." erscheint nur dann. Nachgezogen bei previewUpdated/
   *       motifChanged (Katalog-Deep-Link löst erst nach dem Laden auf; Kunde wechselt das Motiv → Hinweis weg).
   * Fehler hier dürfen den Konfigurator nie aufhalten — alles in try/catch, nichts davon ist kritisch.
   */
  setupNoBrainer() {
    try {
      const strip = this.querySelector('[data-ttg-nb-steps]');
      if (strip && this.engine) {
        const note = this.querySelector('[data-ttg-nb-done-note]');
        const mark = () => {
          const e = this.engine;
          const pre = !!(e && e._motifFromUrl && e.state && e.state.motif && !e.state.noMotif);
          strip.classList.toggle('is-done', pre);
          if (note) note.hidden = !pre;
        };
        mark();
        // previewUpdated deckt den nachgezogenen Katalog-Deep-Link ab (_afterCatalogLoaded setzt _motifFromUrl
        // und rendert die Vorschau, ohne motifChanged zu senden); motifChanged den Wechsel durch den Kunden.
        ['motifChanged', 'previewUpdated'].forEach((ev) => this.engine.on(ev, mark));
      }
    } catch (e) { console.warn('[ttg] No-Brainer-Streifen:', e && e.message); }
  }

  /**
   * Messung (optional). Wird NACH dem vollständigen Mount und nur dann geladen,
   * wenn der Theme-Editor sie eingeschaltet hat — sie liegt damit garantiert
   * außerhalb des kritischen Pfads und kann den Konfigurator nicht aufhalten.
   * Fehler beim Laden bleiben folgenlos: kein Messen ist besser als kein Shop.
   */
  setupMetrics() {
    const src = this.getAttribute('data-metrics-src');
    if (!src || !this.getAttribute('data-metrics-endpoint') || !this.engine) return;
    import(src)
      .then((m) => m.setupMetrics(this, this.engine))
      .catch((e) => console.warn('[ttg] Messung nicht geladen:', e && e.message));
  }

  /** Erfolgsfall: Notbremse lösen und eine etwaige Fehlmarkierung zurücknehmen
   *  (der Timeout kann gefeuert haben, während ein sehr langsamer Import doch
   *  noch durchlief). */
  _bootDone() {
    if (this._bootTimer) { clearTimeout(this._bootTimer); this._bootTimer = null; }
    this.removeAttribute('data-ttg-failed');
  }

  /**
   * Gescheitert — EIN Weg für alle Abbruchgründe. Markiert das Element
   * (`data-ttg-failed`), womit das CSS das tote SSR-Skelett ausblendet, und
   * stellt sicher, dass die native Produktseite sichtbar bleibt. Der Kunde
   * bekommt damit immer entweder einen vollständigen Konfigurator ODER eine
   * vollständige, kaufbare Produktseite — nie eine leere Fläche.
   * Idempotent: ein spät eintreffender Erfolg hebt die Markierung wieder auf.
   */
  _fail(reason) {
    if (this._bootTimer) { clearTimeout(this._bootTimer); this._bootTimer = null; }
    if (this.dataset.ttgFailed) return;
    this.setAttribute('data-ttg-failed', reason || 'unknown');
    document.body.classList.remove('ttg-active');
    console.error('[ttg] Konfigurator nicht verfügbar (' + reason + ') — native Produktseite bleibt stehen');
  }

  /**
   * Obere Sperrzone für die schwebende Mini-Vorschau (Engine liest die
   * CSS-Variable --ttg-mini-inset-top): Horizon-Header ist sticky (scroll-up)
   * und schiebt sich beim Hochscrollen über den Inhalt — die Kachel darf nicht
   * dahinter geparkt werden. Theme-spezifisch, deshalb hier im Adapter.
   */
  setMiniInsetTop() {
    const header = document.getElementById('header-component');
    if (!header || !header.hasAttribute('sticky')) return;
    const h = header.getBoundingClientRect().height;
    if (h > 0) this.style.setProperty('--ttg-mini-inset-top', Math.round(h + 8) + 'px');
  }

  /**
   * Produktgalerie unter der Live-Vorschau: Klick auf ein Thumb zeigt das Foto
   * groß in der Bühne; jede Konfig-Auswahl (Engine-Events) schaltet zurück auf
   * die Live-Vorschau. Chevrons scrollen die Thumb-Leiste.
   */
  setupGallery() {
    const stage = this.querySelector('.ttg__stage');
    const photo = this.querySelector('[data-ttg-stage-photo]');
    const track = this.querySelector('[data-ttg-gal-track]');
    if (!stage || !photo) return;

    // Karussell: Index 0 = Live-Vorschau, 1..n = gefilterte Produktfotos.
    const thumbs = track ? Array.from(track.querySelectorAll('[data-ttg-gal-src]')) : [];
    const items = thumbs.map((t) => ({ src: t.dataset.ttgGalSrc, alt: t.dataset.ttgGalAlt || '' }));
    const prev = this.querySelector('[data-ttg-stage-prev]');
    const next = this.querySelector('[data-ttg-stage-next]');
    let index = 0;

    const render = () => {
      if (index <= 0) {
        index = 0;
        photo.hidden = true;
        photo.removeAttribute('src');
      } else {
        const it = items[index - 1];
        photo.src = it.src;
        photo.alt = it.alt;
        photo.hidden = false;
      }
      thumbs.forEach((t, i) => t.setAttribute('aria-pressed', String(i === index - 1)));
      if (prev) prev.hidden = index <= 0;
      if (next) next.hidden = index >= items.length;
    };
    const setIndex = (i) => { index = Math.max(0, Math.min(items.length, i)); render(); };

    if (prev) prev.addEventListener('click', () => setIndex(index - 1));
    if (next) next.addEventListener('click', () => setIndex(index + 1));

    // Wischen auf der Bühne (Handy, 23.10.2026) — Logik wie Baukasten (cddcb2e): Touch-Events statt Pointer, weil iOS Safari
    // schräge Waagerecht-Gesten bei Pointer-Events abbricht. Klar waagerecht (> 10 px) sperrt die Geste und verhindert das
    // Scrollen; senkrecht gehört sie der Seite, zwei Finger verwerfen sie, Maus wischt nicht. Blättert über setIndex wie die Chevrons.
    if (items.length) {
      let start = null;
      stage.addEventListener('touchstart', (e) => {
        const t = e.touches;
        start = t.length === 1 && !e.target.closest('[data-ttg-stage-prev], [data-ttg-stage-next]') ? { x: t[0].clientX, y: t[0].clientY, quer: false } : null;
      }, { passive: true });
      stage.addEventListener('touchmove', (e) => {
        if (!start) return;
        if (e.touches.length > 1) { start = null; return; }
        const dx = e.touches[0].clientX - start.x, dy = e.touches[0].clientY - start.y;
        if (!start.quer) {
          if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) start.quer = true;
          else if (Math.abs(dy) > 10) { start = null; return; }        // senkrecht: Seite scrollt
        }
        if (start.quer && e.cancelable) e.preventDefault();
      }, { passive: false });
      stage.addEventListener('touchend', (e) => {
        const s = start, t = e.changedTouches[0];
        start = null;
        if (!s || !s.quer || !t || e.touches.length) return;
        const dx = t.clientX - s.x;
        if (Math.abs(dx) > 45) setIndex(index + (dx < 0 ? 1 : -1));
      });
      stage.addEventListener('touchcancel', () => { start = null; });
    }
    if (track) {
      track.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ttg-gal-src]');
        if (!btn) return;
        setIndex(thumbs.indexOf(btn) + 1);
      });
    }

    // Jede Konfig-Auswahl springt zurück auf Position 0 (Live-Vorschau).
    if (this.engine) {
      ['colorChanged', 'motifChanged', 'fontChanged', 'textChanged', 'printChanged', 'variantChanged']
        .forEach((ev) => this.engine.on(ev, () => setIndex(0)));
    }
    render();
  }

  disconnectedCallback() {
    document.body.classList.remove('ttg-active');
  }

  /** Findet das native Produktformular (theme-spezifisch). */
  getProductForm() {
    return document.querySelector(FORM_SELECTOR)
      || document.querySelector('product-form-component form');
  }

  /** Cart-Handover B: native Form füttern und absenden. */
  addToCart(payload) {
    const { variantId, quantity, properties, surcharges, previewPending, previewGrund } = payload;
    const form = this.getProductForm();
    if (!form) {
      console.error('[ttg] Kein Produktformular gefunden — Cart-Handover nicht möglich.');
      return;
    }

    // Wiederholversuch des Warenkorb-Wächters (16.10.2026, Anlass #1006070/#1006054):
    // dieselbe Gruppe wie der erste Add (retryGroup), kein neuer Vorschau-Auftrag —
    // der Auftrag des ersten Versuchs liegt schon im Stash und bleibt gültig. Vorher
    // bekam der zweite Add eine neue Gruppe und einen zweiten Auftrag.
    const letzter = this._lastAdd;
    const retryGroup = (payload.retry && payload.groupId && letzter && letzter.groupId === payload.groupId)
      ? payload.groupId : null;
    // Läuft der erste Add noch (weder cart:update noch cart:error seitdem), keinen
    // zweiten schicken: mit gleicher Gruppe und gleichen Properties legte Shopify ihn
    // auf DIESELBE Zeile (Menge 2). Die Engine prüft danach einfach weiter.
    if (retryGroup && letzter.laeuft) {
      console.info('[ttg] cart-verify: Wiederholversuch ausgelassen, erster Add läuft noch · group=' + retryGroup);
      return { groupId: retryGroup, ausgelassen: 'laeuft' };
    }

    // Gruppen-ID pro Konfigurator-Add: verknüpft die Hauptlinie mit ihren
    // Zuschlagslinien (verstecktes _ttg_group). Das Cart-Template gruppiert
    // darüber robust — auch bei mehreren Konfigurator-Artikeln im Warenkorb,
    // unabhängig von der rohen Warenkorb-Reihenfolge.
    const groupId = retryGroup || ('g' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36));
    // Die Engine liest die Gruppe für die Warenkorb-Prüfung und den Wiederholversuch.
    payload.groupId = groupId;

    // Vorschaubild NICHT-blockierend: Der Artikel geht sofort in den Warenkorb;
    // den (ggf. langsamen) Upload macht ttg-preview-uploader nach der Navigation
    // auf der /cart-Seite und trägt _preview_image per _ttg_group an die Zeile
    // nach. Snapshot (dataURL) + Zuordnung hier stashen, BEVOR wir navigieren.
    // vorschauGrund: warum kein Snapshot/Stash entstand (Engine liefert
    // timeout/font:/dataurl:, hier kommt 'stash' dazu) → _preview_grund unten.
    // Beim Wiederholversuch gilt der Grund des ersten Versuchs, gestasht wird nicht.
    let vorschauGrund = retryGroup ? (letzter.vorschauGrund || null) : (previewGrund || null);
    if (!retryGroup && previewPending && previewPending.dataUrl) {
      try {
        const auftrag = {
          group: groupId,
          dataUrl: previewPending.dataUrl,
          url: previewPending.url,
          property: previewPending.property || '_preview_image',
          maxUploadBytes: previewPending.maxUploadBytes || 0,
        };
        // Anhängen statt überschreiben (13.10.2026, Anlass #1006054: zweites Add
        // 6 s später überschrieb den Auftrag der ersten Zeile). Ohne Uploader
        // (Skript nicht geladen) der alte Einzelslot — Verhalten wie vorher.
        const up = window.__ttgPreviewUploader;
        if (up && typeof up.stash === 'function') {
          if (!up.stash(auftrag)) throw new Error('Stash nicht geschrieben');
        } else {
          sessionStorage.setItem('ttg:pendingPreview', JSON.stringify(auftrag));
        }
      } catch (e) {
        // sessionStorage voll/deaktiviert → kein Nachtrag; Produktbild-Fallback greift.
        console.warn('[ttg] Vorschau-Stash übersprungen:', e && e.message);
        vorschauGrund = 'stash';
      }
    }

    // Options-Aufpreise (surcharge): erst nach ERFOLGREICHEM Haupt-Add
    // (cart:update) als eigene Zeile(n) mitlegen — nie davor, damit kein
    // Zuschlag ohne Hauptartikel im Warenkorb landen kann.
    this._pendingSurcharges = (surcharges && surcharges.length)
      ? { items: surcharges.slice(), quantity: quantity || 1, groupId }
      : null;
    // Zusatzartikel (Schema v1.31, payload.addons): ebenso erst nach dem Haupt-Add, aber als
    // EIGENSTÄNDIGE Zeilen ohne Properties — kein _ttg_group, damit der Reconciler sie nicht an
    // die Hauptmenge koppelt und die Rabattfunktion sie wie einen normalen Artikel sieht.
    // Beim Wiederholversuch nicht erneut (sonst doppelt).
    this._pendingAddons = (!retryGroup && payload.addons && payload.addons.length)
      ? payload.addons.slice()
      : null;

    // Hauptlinie trägt die Gruppen-ID (versteckt, für die Cart-Gruppierung).
    const mainProps = Object.assign({}, properties, { _ttg_group: groupId });

    // Markierung „Vorschau fehlt" (23.09.2026, Anlass #1005955): Jede Hauptzeile
    // eines Produkts mit Vorschau-Upload geht MIT _preview_missing=1 in den
    // Warenkorb. ttg-preview-uploader entfernt sie im selben change.js, das
    // _preview_image setzt. Bleibt der Nachtrag aus (Upload-Host nicht erreichbar,
    // kein Snapshot, Kasse vor dem Upload), trägt die Bestellung die Markierung
    // statt still ohne Bild auszusehen. Zuschlagszeilen bekommen sie nie — sie
    // entstehen unten aus eigenen Properties.
    const pu = this.engine && this.engine.config && this.engine.config.previewUpload;
    if (pu && pu.enabled && pu.url) mainProps._preview_missing = '1';
    // Grund dazu (03.10.2026, Anlass #1006008): nur wenn beim Add kein Snapshot
    // oder kein Stash entstand. Fehlt das Bild später OHNE _preview_grund, lag
    // es am Upload oder am change.js. Der Uploader entfernt beide zusammen.
    if (pu && pu.enabled && pu.url && vorschauGrund) mainProps._preview_grund = vorschauGrund;

    // 1) Variante setzen
    const idInput = form.querySelector('input[name="id"]');
    if (idInput && variantId != null) idInput.value = String(variantId);

    // 1b) Menge ins native Mengen-Feld (Fallback: hidden input anlegen)
    if (quantity != null) {
      let qtyInput = form.querySelector('[name="quantity"]');
      if (!qtyInput) {
        qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = 'quantity';
        form.appendChild(qtyInput);
      }
      qtyInput.value = String(quantity);
    }

    // 2) Sichtbare + versteckte Properties als hidden inputs in DAS Form
    this.syncProperties(form, mainProps);

    // 3) Native Add-to-Cart-Buttons für die (verfügbare) Variante aktivieren
    form.querySelectorAll('[ref="addToCartButton"], button[name="add"]').forEach((b) => {
      if (b) b.disabled = false;
    });

    // 3b) Seiten-Modus: nach erfolgreichem Add (cart:update) zur /cart-Seite.
    if (this.dataset.cartType === 'page') this._awaitCartPageNav = true;

    // Merker für einen Wiederholversuch: Gruppe, Grund, läuft bis cart:update/cart:error.
    this._lastAdd = { groupId, vorschauGrund, laeuft: true };

    // 4) Theme-Submit auslösen → product-form.js übernimmt fetch + Drawer
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
    return { groupId };
  }

  /** Ersetzt vorhandene ttg-Properties und schreibt die aktuellen hinein. */
  syncProperties(form, properties) {
    form.querySelectorAll('input[data-ttg-prop]').forEach((n) => n.remove());
    Object.keys(properties).forEach((key) => {
      const value = properties[key];
      if (value == null || value === '') return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + key + ']';
      input.value = value;
      input.setAttribute('data-ttg-prop', '');
      form.appendChild(input);
    });
  }

  /** Theme-Cart-Event → Engine-Event (Naht für spätere Analytics-Consumer). */
  bindCartEvents() {
    document.addEventListener('cart:update', async (e) => {
      if (this._lastAdd) this._lastAdd.laeuft = false;
      if (this.engine) this.engine.emit('cartAdded', { source: 'theme', detail: e.detail });
      // Options-Aufpreise: der Hauptartikel ist jetzt im Warenkorb → Zuschlags-
      // Produkt(e) als eigene Zeile(n) nachlegen. Läuft VOR der Seiten-Navigation,
      // damit die /cart-Seite beide Zeilen zeigt. (Unser eigener /cart/add.js-
      // Fetch feuert kein Theme-cart:update — keine Schleife. Hinweis: Im
      // Drawer-Modus zeigt der bereits geöffnete Drawer die Zuschlags-Zeile erst
      // nach dem nächsten Cart-Refresh; aktuell ist cart_type = "page".)
      if (this._pendingSurcharges) {
        const pending = this._pendingSurcharges;
        this._pendingSurcharges = null;
        await this.addSurcharges(pending);
      }
      if (this._pendingAddons) {
        const zusatz = this._pendingAddons;
        this._pendingAddons = null;
        await this.addAddons(zusatz);
      }
      // Warenkorb als Seite (Theme-Setting cart_type = "page"): Horizon macht
      // nach dem AJAX-Add keinen Redirect — für den Konfigurator-Flow navigieren
      // WIR nach unserem eigenen, erfolgreichen Add zur /cart-Seite. Im
      // Drawer-Modus (data-cart-type="drawer") bleibt alles wie bisher.
      if (this._awaitCartPageNav) {
        this._awaitCartPageNav = false;
        window.location.assign((window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart');
      }
    });
    document.addEventListener('cart:error', () => {
      if (this._lastAdd) this._lastAdd.laeuft = false;
      this._awaitCartPageNav = false;
      this._pendingSurcharges = null;
      this._pendingAddons = null;
    });
  }

  /**
   * Zuschlags-Produkte (Options-Aufpreise) als eigene Warenkorb-Zeilen mitlegen.
   * Fehlertolerant (Pflicht laut Schema §3.1/Regel 6): der Hauptartikel liegt zu
   * diesem Zeitpunkt bereits im Warenkorb — ein Fehlschlag hier darf den Kauf
   * nie blockieren, aber auch nicht still bleiben (Konsole + Hinweis an den
   * Kunden). Referenz-Property macht die Zuordnung in der Bestellung eindeutig.
   */
  async addSurcharges({ items, quantity, groupId }) {
    const titleEl = this.querySelector('.ttg__title');
    const mainTitle = (titleEl ? titleEl.textContent : document.title).trim();
    const lines = [];
    items.forEach((sc) => {
      // gid://shopify/ProductVariant/123… → numerische ID für /cart/add.js
      const num = String(sc.variantId).match(/(\d+)\s*$/);
      if (!num) { console.error('[ttg] Zuschlag: unbrauchbare variantId', sc.variantId); return; }
      // Alle Kennungen versteckt (‚_'-Präfix): am Kunden-Warenkorb ausgeblendet,
      // im Admin/der Bestellung aber sichtbar (Produktions-Referenz). Das
      // Cart-Template liest _ttg_group (Gruppierung), _ttg_role (Zuschlagszeile),
      // _ttg_kind/_ttg_value (warmes Label) und blendet sie aus der Anzeige aus.
      const props = {
        _ttg_group: groupId || '',
        _ttg_role: 'surcharge',
        _ttg_kind: sc.kind || '',
        _ttg_value: sc.choice || '',
        _ttg_ref: mainTitle + ' — ' + (sc.option || 'Option') + ': ' + (sc.choice || ''),
      };
      lines.push({ id: Number(num[1]), quantity: quantity || 1, properties: props });
    });
    if (!lines.length) return;
    try {
      let url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';
      if (!/\.js$/.test(url)) url += '.js';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: lines }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
    } catch (e) {
      console.error('[ttg] Zuschlag-Add fehlgeschlagen (Hauptartikel ist im Warenkorb):', e);
      const labels = items.map((i) => i.priceLabel).filter(Boolean).join(', ');
      window.alert('Dein Artikel liegt im Warenkorb! Nur der Aufpreis für Deine Farbwahl'
        + (labels ? ' (' + labels + ')' : '')
        + ' konnte gerade nicht automatisch hinzugefügt werden. Deine Bestellung geht trotzdem durch — wir prüfen und ergänzen das bei Bedarf im Anschluss.');
    }
  }

  /** Zusatzartikel (Schema v1.31) als eigene, property-lose Warenkorbzeilen. Scheitert der Add,
   *  liegt der Hauptartikel trotzdem im Warenkorb — Hinweis an den Kunden, kein stilles Verschlucken. */
  async addAddons(items) {
    const lines = [];
    items.forEach((it) => {
      const num = String(it.variantId).match(/(\d+)\s*$/);
      if (!num) { console.error('[ttg] Zusatzartikel: unbrauchbare variantId', it.variantId); return; }
      lines.push({ id: Number(num[1]), quantity: it.quantity || 1 });
    });
    if (!lines.length) return;
    try {
      let url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';
      if (!/\.js$/.test(url)) url += '.js';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: lines }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
    } catch (e) {
      console.error('[ttg] Zusatzartikel-Add fehlgeschlagen (Hauptartikel ist im Warenkorb):', e);
      const namen = items.map((i) => i.label).filter(Boolean).join(', ');
      window.alert('Dein Artikel liegt im Warenkorb! Der Zusatzartikel'
        + (namen ? ' (' + namen + ')' : '')
        + ' konnte gerade nicht hinzugefügt werden — bitte leg ihn im Warenkorb noch einmal dazu.');
    }
  }

}

if (!customElements.get('ttg-customizer')) {
  customElements.define('ttg-customizer', TTGCustomizerElement);
}
